import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import SaveButton from 'common/components/SaveButton';
import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import BackdropDialog from 'common/components/BackdropDialog';
import { Routes, replace } from 'routing';
import * as ActiveDiscountSelectors from 'selectors/ActiveDiscountSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ActiveDiscountActions from 'actions/ActiveDiscountActions';
import * as NotificationActions from 'actions/NotificationActions';
import EmptyState from 'common/components/EmptyState';
import { Typography } from '@mui/material';
import { isUniqueShopDiscountCode } from 'views/Products/__utils/creatingDiscountCodeUtils';
import { useTranslation } from 'services/localization/useTranslation';
import EditDiscount from './EditDiscount';
import { validateCode, validateDiscountPriceRule } from '../validations';

type ListenerRef = (() => void) | null;

export type DiscountFormErrors = {
	name?: string;
	value?: string;
};

const EditDiscountCode = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const activeDiscount = useSelector(ActiveDiscountSelectors.discountCodeData);
	const activeDiscountWithChanges = useSelector(ActiveDiscountSelectors.discountCodeWithChanges);
	const activeShopId = useSelector(ShopSelectors.activeShopId)!;
	const { discountId } = useParams<{ discountId: string }>();
	const hasChanges = useSelector(ActiveDiscountSelectors.hasDiscountChanges);
	const isSaving = useSelector(ActiveDiscountSelectors.isSaving);
	const localChanges = useSelector(ActiveDiscountSelectors.localChanges);
	const discountCodeLoading = useSelector(ActiveDiscountSelectors.discountCodeLoading);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
	const [formErrors, setFormErrors] = useState<DiscountFormErrors>({});

	const discountListener = useRef<ListenerRef>(null);
	useEffect(() => {
		if (discountId) {
			if (discountListener.current === null) {
				discountListener.current = dispatch(
					ActiveDiscountActions.setDiscountListener(discountId),
				) as ListenerRef;
			}
		} else {
			replace(Routes.CATALOG_CREATE_DISCOUNT);
		}
	}, [dispatch, discountId]);

	useEffect(() => {
		/** React might cleanup these refs before they can be called in our own cleanup below,
		 * so make sure we still have access to them */
		const removeDiscountListener = () => discountListener.current?.();

		return () => {
			removeDiscountListener();
			dispatch(ActiveDiscountActions.reset());
		};
	}, [dispatch]);

	const getUpdatedCode = () => {
		const updatedCode = localChanges.code;
		if (updatedCode && updatedCode !== activeDiscount?.code) {
			return updatedCode;
		}
		return undefined;
	};

	const areChangesValid = async () => {
		const updatedCode = getUpdatedCode();
		if (updatedCode) {
			const isValid = await isUniqueShopDiscountCode(updatedCode, activeShopId);
			if (!isValid) {
				setFormErrors({
					...formErrors,
					name: t(
						'discount.codeWithNameAlreadyExists',
						'A code with this name already exists. Try a new code.',
					),
				});
				return false;
			}
			const codeError = validateCode(updatedCode, t);
			if (codeError) {
				setFormErrors({
					...formErrors,
					name: codeError,
				});
				return false;
			}
		}
		const valueError = validateDiscountPriceRule(activeDiscountWithChanges.priceRule, t);
		if (valueError) {
			setFormErrors({
				...formErrors,
				value: valueError,
			});
			return false;
		}
		return true;
	};

	const handleSave = async () => {
		try {
			if (await areChangesValid()) {
				dispatch(ActiveDiscountActions.saveChanges());
				setFormErrors({});
			}
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	const handleDelete = async () => {
		setIsDeleting(true);
		dispatch(ActiveDiscountActions.deleteActiveDiscount());
	};

	return (
		<>
			<SetPageNavAction
				backAction={{
					to: Routes.CatalogTab('discounts'),
					title: t('discount.discounts', 'Discounts'),
				}}
			/>
			<EmptyState
				title={'Discount not found'}
				isLoading={discountCodeLoading || isDeleting}
				isEmpty={!activeDiscount}
				callToActionText={t('discount.backToDiscountsPage', 'Back to discounts page')}
				callToActionHandler={() => replace(Routes.CatalogTab('discounts'))}
				render={() => (
					<>
						<SetPageTitle
							priority={2}
							title={activeDiscount ? activeDiscount.code : ''}
							rightContent={
								<SaveButton disabled={!hasChanges} loading={isSaving} onClick={handleSave} t={t} />
							}
							contextActions={[
								{
									label: t('common:actions.delete', 'Delete'),
									onClick: () => setDeleteDialogOpen(true),
								},
							]}
						/>
						<EditDiscount formErrors={formErrors} />
					</>
				)}
			/>
			<BackdropDialog
				isOpen={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
				label={t('discount.deleteDiscountWarningTitle', 'Delete discount code?')}
				body={
					<Typography variant="body1">
						{t(
							'discount.deleteDiscountWarningDescription',
							'You wont be able to recover this discount code if you delete it.',
						)}
					</Typography>
				}
				variant="danger"
				confirm={{
					label: t('discount.deleteDiscountConfirm', 'Yes, delete it'),
					callback: handleDelete,
					closeAfterDone: true,
				}}
				cancel={{
					label: t('discount.deleteDiscountCancel', 'No, go back'),
				}}
			/>
		</>
	);
};

export default EditDiscountCode;
